import React from 'react';
import { Card, Row, Col, Table, Progress, Typography, Tag } from 'antd';
import { CreditCardOutlined, AlertOutlined } from '@ant-design/icons';
import AnalyticsCard from '../../components/shared/AnalyticsCard';
import ChartContainer from '../../components/shared/ChartContainer';
import { mockRevenueData, mockPaymentAnalysis } from '../data/mockData';

const { Text } = Typography;

const PaymentAnalysis: React.FC = () => {
  const { payments } = mockRevenueData;

  // Payment methods chart
  const paymentMethodsData = {
    labels: payments.paymentMethods.map(item => item.method),
    datasets: [
      {
        label: 'Transaction Count',
        data: payments.paymentMethods.map(item => item.count),
        backgroundColor: ['#1890ff', '#52c41a', '#faad14', '#722ed1'],
      },
    ],
  };

  // Payment timeline chart
  const paymentTimelineData = {
    labels: mockPaymentAnalysis.map(item => item.period),
    datasets: [
      {
        label: 'On Time',
        data: mockPaymentAnalysis.map(item => item.onTime),
        backgroundColor: '#52c41a',
      },
      {
        label: 'Late',
        data: mockPaymentAnalysis.map(item => item.late),
        backgroundColor: '#faad14',
      },
      {
        label: 'Failed',
        data: mockPaymentAnalysis.map(item => item.failed),
        backgroundColor: '#f5222d',
      },
    ],
  };

  // Table columns for payment analysis
  const paymentColumns = [
    {
      title: 'Payment Method',
      dataIndex: 'method',
      key: 'method',
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (percentage: number) => `${percentage.toFixed(1)}%`,
    },
    {
      title: 'Avg Amount',
      dataIndex: 'avgAmount',
      key: 'avgAmount',
      render: (amount: number) => `$${amount.toLocaleString()}`,
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <AnalyticsCard
            title="Outstanding Invoices"
            value={payments.outstandingInvoices}
            format="currency"
            trend="down"
            change={-12.5}
            subtitle="vs last month"
          />
        </Col>
        <Col span={6}>
          <AnalyticsCard
            title="Overdue Payments"
            value={payments.overduePayments}
            format="currency"
            trend="down"
            change={-18.3}
            subtitle="vs last month"
          />
        </Col>
        <Col span={6}>
          <AnalyticsCard
            title="Average Payment Time"
            value={payments.averagePaymentTime}
            format="number"
            trend="stable"
            change={0.3}
            subtitle="days"
          />
        </Col>
        <Col span={6}>
          <AnalyticsCard
            title="Payment Success Rate"
            value={94.2}
            format="percentage"
            trend="up"
            change={2.1}
            subtitle="vs last month"
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <ChartContainer
            title={
              <span>
                <CreditCardOutlined /> Payment Methods Distribution
              </span>
            }
            type="pie"
            data={paymentMethodsData}
            height={300}
          />
        </Col>
        <Col span={12}>
          <ChartContainer
            title="Payment Status Trends"
            type="bar"
            data={paymentTimelineData}
            height={300}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card 
            title={
              <span>
                <AlertOutlined /> Payment Method Details
              </span>
            }
          >
            <Table
              columns={paymentColumns}
              dataSource={payments.paymentMethods}
              rowKey="method"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Payment Performance Metrics">
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>On-Time Payment Rate</Text>
                <Text strong>88.9%</Text>
              </div>
              <Progress percent={88.9} strokeColor="#52c41a" />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>Late Payment Rate</Text>
                <Text strong>8.7%</Text>
              </div>
              <Progress percent={8.7} strokeColor="#faad14" />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>Failed Payment Rate</Text>
                <Text strong>2.4%</Text>
              </div>
              <Progress percent={2.4} strokeColor="#f5222d" />
            </div>

            <div style={{ marginTop: 20 }}>
              <Text strong>Payment Alerts</Text>
              <div style={{ marginTop: 8 }}>
                <Tag color="warning" style={{ marginBottom: 8 }}>
                  15 invoices overdue &gt; 30 days
                </Tag>
                <br />
                <Tag color="error" style={{ marginBottom: 8 }}>
                  3 failed payment retries needed
                </Tag>
                <br />
                <Tag color="success">
                  Payment success rate above target
                </Tag>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PaymentAnalysis; 